// Logique de calcul des variants d'une carte
// Source de vérité : le champ card.variants de TCGdex, complété par rareté
// uniquement quand TCGdex ne renseigne que 'normal'.

import { LABELS, VARIANT_ORDER, REVERSE_RARITIES } from "../config/variants";

// Ces cartes n'existent jamais en reverse, or TCGdex classe parfois les EX en
// « Rare ». Réplique de NO_REVERSE_NAME_RE (server.py), à garder synchronisée.
const NO_REVERSE_NAME_RE = /(?:^|[\s-])(?:EX|GX|V|VMAX|VSTAR)$/i;

export const makeVariant = (type) => ({
  key: `v-${type}`,
  type,
  label: LABELS[type] || type,
  raw: { type },
});

function variantsFromFlags(flags) {
  return VARIANT_ORDER.filter(t => flags[t] === true).map(makeVariant);
}

// Vrai si un type de variante est une variante perso (créée par l'utilisateur).
export const isCustomVariant = (type) => typeof type === "string" && type.startsWith("custom:");

// Variantes perso attachées à la carte par le backend (champ dénormalisé
// custom_variants : [{id, name, abbr, color}]). Elles s'ajoutent EN PLUS des
// variantes standard. Le type est "custom:<id>" pour rester une chaîne unique
// dans les clés de collection (cardId::custom:<id>).
function customVariantEntries(card) {
  const list = Array.isArray(card?.custom_variants) ? card.custom_variants : [];
  return list.map(cv => ({
    key: `v-custom:${cv.id}`,
    type: `custom:${cv.id}`,
    label: cv.name,
    custom: { abbr: cv.abbr, color: cv.color },
    raw: { type: `custom:${cv.id}` },
  }));
}

export function computeVariants(card) {
  if (!card) return [];

  const custom = customVariantEntries(card);

  // 1. Override manuel utilisateur (tableau de types) → prioritaire absolu
  if (Array.isArray(card.variants_override)) {
    const set = new Set(card.variants_override);
    const ordered = VARIANT_ORDER.filter(t => set.has(t)).map(makeVariant);
    if (ordered.length > 0) return [...ordered, ...custom];
    // Toutes les variantes standard décochées, seules des variantes perso restent :
    // on les prend telles quelles, sans réinventer une « Normale ». Si ce compte
    // n'a pas ces variantes perso, l'override ne lui dit rien : suite des règles.
    if (custom.some(c => set.has(c.type))) return custom;
  }

  // 2. Champ TCGdex "variants" (objet de booléens)
  const flags = card.variants;
  if (flags && typeof flags === "object" && !Array.isArray(flags)) {
    let result = variantsFromFlags(flags);

    // 2b. Complétion par rareté : si TCGdex ne donne QUE 'normal'
    // (typique des blocs anciens mal renseignés), on ajoute Reverse
    // pour les raretés qui existent en reverse. On ne touche PAS aux
    // cartes qui ont déjà d'autres variants (reverse/holo déjà présents).
    const onlyNormal = result.length === 1 && result[0].type === "normal";
    if (onlyNormal && REVERSE_RARITIES.has(card.rarity || "") && !NO_REVERSE_NAME_RE.test(card.name || "")) {
      result = [makeVariant("normal"), makeVariant("reverse")];
    }

    const base = result.length > 0 ? result : [makeVariant("normal")];
    return [...base, ...custom];
  }

  // 3. Carte pas encore enrichie (grille, fetch en cours) → état chargement neutre
  return [{ key: "v-loading", type: "loading", label: "…", raw: { type: "loading" }, loading: true }];
}

// Variantes à afficher et compter selon le mode du set ; liste vide = la carte ne
// fait pas partie du set dans ce mode. Réplique de counted_variant_types (server.py).
// card.master_plus_only : ce que ce compte a réservé au Master Set +, ["*"] = tout.
export function variantsForMode(card, mode) {
  const all = computeVariants(card);
  if (mode === "masterplus") return all;
  const only = card?.master_plus_only || [];
  if (only.includes("*")) return [];
  const kept = all.filter(v => !isCustomVariant(v.type) && !only.includes(v.type));
  if (mode === "master" || kept.length === 0) return kept;
  return [...kept, ...all.filter(v => isCustomVariant(v.type))];
}

// Style visuel (Tailwind) d'une puce de variant dans le modal détaillé
export const variantStyle = (type) => {
  switch (type) {
    case "holo":    return { bg: "bg-gradient-to-br from-purple-500 to-pink-500", ring: "ring-pink-400" };
    case "cosmos":  return { bg: "bg-gradient-to-br from-indigo-500 to-fuchsia-500", ring: "ring-fuchsia-400" };
    case "rgb":     return { bg: "bg-gradient-to-br from-red-500 via-emerald-500 to-blue-500", ring: "ring-emerald-400" };
    case "reverse": return { bg: "bg-gradient-to-br from-blue-500 to-cyan-400",  ring: "ring-cyan-400" };
    case "wPromo":  return { bg: "bg-gradient-to-br from-red-500 to-orange-600", ring: "ring-red-400" };
    case "normal":  return { bg: "bg-zinc-600", ring: "ring-zinc-400" };
    default:        return { bg: "bg-gradient-to-br from-amber-500 to-orange-500", ring: "ring-amber-400" };
  }
};
